import { useState, useEffect } from 'react'
import mixpanel from 'mixpanel-browser'
import { Bell, Check, X, Trash2, CheckCheck, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useNotifications } from '@/hooks/useNotifications'
import { useInvites } from '@/hooks/useInvites'
import { useOrganizationContext } from '@/contexts/OrganizationContext'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'

type NotificationData = {
  id: string
  user_id: string
  type: string
  title: string
  message: string
  data: any
  is_read: boolean
  created_at: string
  updated_at: string
}

interface NotificationsDropdownProps {
  asModal?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function NotificationsDropdown({ asModal, open, onOpenChange }: NotificationsDropdownProps = {}) {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications()
  const { acceptInvite, declineInvite } = useInvites()
  const { currentOrg } = useOrganizationContext()
  const [processingInvite, setProcessingInvite] = useState<string | null>(null)
  const navigate = useNavigate()

  // Calcular contador baseado nas notificações locais (fonte de verdade)
  // Isso garante que o contador sempre reflita o estado real das notificações
  const actualUnreadCount = notifications.filter(n => !n.is_read).length

  useEffect(() => {
    if (open) mixpanel.track('app_notifications_opened', { unread: actualUnreadCount })
  }, [open])


  const handleAcceptInvite = async (notification: NotificationData) => {
    if (!notification.data?.invite_id) return
    mixpanel.track('app_notifications_invite_accepted')
    try {
      setProcessingInvite(notification.data.invite_id)
      await acceptInvite(notification.data.invite_id)
      await markAsRead(notification.id)
      
      toast({
        title: "Convite aceito!",
        description: `Você agora é membro da organização ${notification.data.org_name}`,
      })
      
      // Refresh organization context to show new org
      window.location.reload()
    } catch (error) {
      toast({
        title: "Erro ao aceitar convite",
        description: "Tente novamente mais tarde",
        variant: "destructive"
      })
    } finally {
      setProcessingInvite(null)
    }
  }

  const handleDeclineInvite = async (notification: NotificationData) => {
    if (!notification.data?.invite_id) return
    mixpanel.track('app_notifications_invite_declined')
    try {
      setProcessingInvite(notification.data.invite_id)
      await declineInvite(notification.data.invite_id)
      await markAsRead(notification.id)
      
      toast({
        title: "Convite recusado",
        description: "O convite foi recusado com sucesso",
      })
    } catch (error) {
      toast({
        title: "Erro ao recusar convite",
        description: "Tente novamente mais tarde",
        variant: "destructive"
      })
    } finally {
      setProcessingInvite(null)
    }
  }

  const handleMarkAsRead = async (notificationId: string) => {
    mixpanel.track('app_notifications_marked_read')
    await markAsRead(notificationId)
  }

  const handleDeleteNotification = async (notificationId: string) => {
    mixpanel.track('app_notifications_deleted')
    await deleteNotification(notificationId)
  }

  const formatNotificationTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) {
      return 'Agora mesmo'
    } else if (diffInHours < 24) {
      return `Há ${diffInHours}h`
    } else {
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    }
  }

  const handleCompleteProfile = async (notification: NotificationData) => {
    // Marcar como lida antes de redirecionar
    await markAsRead(notification.id)
    
    // Redirecionar para o perfil
    navigate('/profile')
  }

  const renderNotificationContent = (notification: NotificationData) => {
    if (notification.type === 'invite') {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.title}</p>
              <p className="text-xs text-muted-foreground">{notification.message}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatNotificationTime(notification.created_at)}
              </p>
            </div>
            {!notification.is_read && (
              <div className="w-2 h-2 bg-blue-500 rounded-full ml-2" />
            )}
          </div>
          
          {!notification.is_read && (
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => handleAcceptInvite(notification)}
                disabled={processingInvite === notification.data?.invite_id}
              >
                <Check className="h-3 w-3 mr-1" />
                Aceitar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => handleDeclineInvite(notification)}
                disabled={processingInvite === notification.data?.invite_id}
              >
                <X className="h-3 w-3 mr-1" />
                Recusar
              </Button>
            </div>
          )}
        </div>
      )
    }

    // Notificação de cadastro incompleto
    if (notification.type === 'system' && notification.data?.action === 'complete_profile') {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.title}</p>
              <p className="text-xs text-muted-foreground">{notification.message}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatNotificationTime(notification.created_at)}
              </p>
            </div>
            {!notification.is_read && (
              <div className="w-2 h-2 bg-blue-500 rounded-full ml-2" />
            )}
          </div>
          
          {!notification.is_read && (
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="default"
                className="h-7 text-xs"
                onClick={() => handleCompleteProfile(notification)}
              >
                <User className="h-3 w-3 mr-1" />
                Finalizar Cadastro
              </Button>
            </div>
          )}
        </div>
      )
    }

    // Default notification rendering
    return (
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium">{notification.title}</p>
          <p className="text-xs text-muted-foreground">{notification.message}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatNotificationTime(notification.created_at)}
          </p>
        </div>
        {!notification.is_read && (
          <div className="w-2 h-2 bg-blue-500 rounded-full ml-2" />
        )}
      </div>
    )
  }

  if (asModal) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-4 py-3 border-b border-border/50">
            <DialogTitle className="flex items-center justify-between">
              <span>Notificações {actualUnreadCount > 0 && `(${actualUnreadCount})`}</span>
              {actualUnreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { mixpanel.track('app_notifications_mark_all_read'); markAllAsRead() }}>
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Marcar todas como lidas
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-2">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Nenhuma notificação
              </div>
            ) : (
              <div className="space-y-1">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="flex flex-col p-3 rounded-lg hover:bg-accent cursor-pointer"
                    onClick={(e) => {
                      if (!notification.is_read && !(e.target as HTMLElement).closest('button')) {
                        handleMarkAsRead(notification.id)
                      }
                    }}
                  >
                    {renderNotificationContent(notification)}
                    <div className="flex gap-1 mt-2 w-full justify-end">
                      {notification.is_read && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                          onClick={(e) => { e.stopPropagation(); handleDeleteNotification(notification.id) }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                      {!notification.is_read && (
                        <Button variant="ghost" size="sm" className="h-6 text-xs"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id) }}>
                          Marcar como lida
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative group">
          <Bell className="h-5 w-5 transition-colors group-hover:text-primary" />
          {actualUnreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold leading-none shadow-sm ring-2 ring-background z-10">
              {actualUnreadCount > 99 ? '99+' : actualUnreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-80 max-h-96 overflow-y-auto" align="end" forceMount>
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificações {actualUnreadCount > 0 && `(${actualUnreadCount})`}</span>
          {actualUnreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => { mixpanel.track('app_notifications_mark_all_read'); markAllAsRead() }}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Marcar todas como lidas
            </Button>
          )}
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Nenhuma notificação
          </div>
        ) : (
          notifications.map((notification) => (
            <DropdownMenuItem
              key={notification.id}
              className="flex flex-col items-start p-3 cursor-pointer"
              onSelect={(e) => {
                // Prevenir seleção padrão do menu item
                e.preventDefault()
              }}
              onClick={(e) => {
                // Se clicar na área da notificação (não nos botões), marcar como lida se não estiver lida
                if (!notification.is_read && !(e.target as HTMLElement).closest('button')) {
                  e.stopPropagation()
                  handleMarkAsRead(notification.id)
                }
              }}
            >
              {renderNotificationContent(notification)}
              
              <div className="flex gap-1 mt-2 w-full justify-end">
                {notification.is_read && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteNotification(notification.id)
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
                {!notification.is_read && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleMarkAsRead(notification.id)
                    }}
                  >
                    Marcar como lida
                  </Button>
                )}
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

